import { CAT_PALETTE } from "./Hooks/useVendorData";
import { TENT_SUBDIVISIONS, getOrientedLayoutStyle } from "./Tentvisuals";

// getOrientedLayoutStyle (from Tentvisuals.js) only covers g11/g12/g22 —
// g31 isn't part of the shared admin/vendor visual language yet. Extend it
// locally here with the same left/right → stacked rule used everywhere else,
// so a 3-way split tile orients consistently with its facing too.
function getFloorPlanGridStyle(layout, facing) {
  if (layout === "g31") {
    return facing === "left" || facing === "right"
      ? { gridTemplateColumns: "1fr", gridTemplateRows: "1fr 1fr 1fr" }
      : { gridTemplateColumns: "1fr 1fr 1fr", gridTemplateRows: "1fr" };
  }
  return getOrientedLayoutStyle(layout, facing);
}

// ── SlotFloorPlan ────────────────────────────────────────────────────────────
// Renders the tile's sub-slots in the same physical arrangement as the real
// tent (2x2 pod, side-by-side halves, 3-way split), each cell showing the
// vendor placed there. Clicking a filled cell jumps straight to that
// vendor's spotlight via onSelect. `facing` orients g12/g31 splits to match
// which edge of the tent is actually open — a left/right-facing tile stacks
// its subs top/bottom instead of side by side, so vendors sharing a tent can
// tell which physical side they're on.
function SlotFloorPlan({ bookings, effectiveType, facing, onSelect }) {
  const def = TENT_SUBDIVISIONS[effectiveType];
  if (!def || def.subs.length === 0) return null;

  const orientedStyle = getFloorPlanGridStyle(def.layout, facing);

  return (
    <div
      className={`vmp-floorplan vmp-floorplan--${def.layout}`}
      style={orientedStyle}
    >
      {def.subs.map(sub => {
        const booking = bookings.find(b => (b.sub_id || "A") === sub);
        return (
          <div
            key={sub}
            className={[
              "vmp-floorplan-cell",
              booking ? "vmp-floorplan-cell--filled" : "vmp-floorplan-cell--empty",
            ].join(" ")}
            onClick={() => booking && onSelect(booking)}
            role={booking ? "button" : undefined}
            aria-label={booking ? booking.brand_name : `${sub} — empty`}
          >
            {booking ? (
              <>
                {booking.logo_url ? (
                  <img
                    src={booking.logo_url}
                    alt={booking.brand_name}
                    className="vmp-floorplan-avatar vmp-floorplan-avatar--img"
                  />
                ) : (
                  <div className="vmp-floorplan-avatar">
                    {booking.brand_name?.[0]?.toUpperCase() || "?"}
                  </div>
                )}
                <span className="vmp-floorplan-name">{booking.brand_name}</span>
              </>
            ) : (
              <span className="vmp-floorplan-empty-lbl">{sub}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── SlotVendorsModal ───────────────────────────────────────────────────────────
export function SlotVendorsModal({ slotId, vendors, effectiveType, facing, catPalette, onSelect, onClose }) {
  return (
    <div
      className="vmp-backdrop"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="vmp-slotlist" role="dialog" aria-modal="true">
        <div className="vmp-slotlist-hd">
          <div>
            <div className="vmp-slotlist-title">Slot {slotId}</div>
            <div className="vmp-slotlist-sub">
              {vendors.length} vendors share this tent
            </div>
          </div>
          <button
            className="vmp-modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path
                d="M3 3l10 10M13 3L3 13"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {effectiveType ? (
          <SlotFloorPlan
            bookings={vendors}
            effectiveType={effectiveType}
            facing={facing}
            onSelect={onSelect}
          />
        ) : (
          // Fallback: no resolvable tent shape — degrade to the flat card list
          // so a booking never becomes unreachable.
          <div className="vmp-slotlist-cards">
            {vendors.map(vendor => {
              const firstCat = vendor.categories?.[0];
              const pal = firstCat
                ? catPalette[firstCat] || CAT_PALETTE[0]
                : CAT_PALETTE[0];
              return (
                <div
                  key={vendor.applicationId}
                  className="vmp-vcard"
                  onClick={() => onSelect(vendor)}
                >
                  <div className="vmp-vc-top">
                    {vendor.logo_url ? (
                      <img
                        src={vendor.logo_url}
                        alt={vendor.brand_name}
                        className="vmp-vc-logo vmp-vc-logo--img"
                      />
                    ) : (
                      <div
                        className="vmp-vc-logo"
                        style={{ background: pal.bg, color: pal.tc }}
                      >
                        {vendor.brand_name?.[0]?.toUpperCase() || "?"}
                      </div>
                    )}
                    <div className="vmp-vc-info">
                      <div className="vmp-vc-name">{vendor.brand_name}</div>
                      {firstCat && (
                        <span
                          className="vmp-cat-pill"
                          style={{ background: pal.bg, color: pal.tc }}
                        >
                          {firstCat}
                        </span>
                      )}
                    </div>
                  </div>
                  {vendor.description && (
                    <div className="vmp-vc-desc">{vendor.description}</div>
                  )}
                  <button
                    className="vmp-readmore-btn"
                    onClick={e => { e.stopPropagation(); onSelect(vendor); }}
                  >
                    View details
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M4 2l4 4-4 4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── SpotlightModal ─────────────────────────────────────────────────────────────
// cameFromList is passed explicitly from the parent so the back button stays
// correct even after the slot-list state is cleared on the path to spotlight.
export function SpotlightModal({ vendor, catPalette, cameFromList, onBack, onClose }) {
  if (!vendor) return null;

  const firstCat = vendor.categories?.[0];
  const pal = firstCat
    ? catPalette[firstCat] || CAT_PALETTE[0]
    : CAT_PALETTE[0];

  return (
    <div
      className="vmp-backdrop"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="vmp-spot"
        role="dialog"
        aria-modal="true"
        aria-label={vendor.brand_name}
      >
        <div
          className="vmp-spot-banner"
          style={{
            background: `linear-gradient(135deg, ${pal.bg} 0%, ${pal.bg}cc 100%)`,
          }}
        >
          {/* Back button — only shown when navigated from a multi-vendor slot list */}
          {cameFromList && (
            <button
              className="vmp-spot-banner-back"
              onClick={onBack}
              aria-label="Back to slot vendors"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L4 8l6 5"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}

          <button
            className="vmp-spot-banner-close"
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
              <path
                d="M3 3l10 10M13 3L3 13"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
              />
            </svg>
          </button>

          <div className="vmp-spot-logo-wrap">
            {vendor.logo_url ? (
              <img
                src={vendor.logo_url}
                alt={vendor.brand_name}
                className="vmp-spot-logo vmp-spot-logo--img"
              />
            ) : (
              <div
                className="vmp-spot-logo"
                style={{ background: pal.bg, color: pal.tc }}
              >
                {vendor.brand_name?.[0]?.toUpperCase() || "?"}
              </div>
            )}
          </div>
        </div>

        <div className="vmp-spot-body">
          <div className="vmp-spot-name">{vendor.brand_name}</div>
          <div className="vmp-spot-slot">Slot {vendor.slotId}</div>

          <div className="vmp-spot-cats">
            {vendor.categories.map(cat => {
              const p = catPalette[cat] || CAT_PALETTE[0];
              return (
                <span
                  key={cat}
                  className="vmp-cat-pill"
                  style={{ background: p.bg, color: p.tc }}
                >
                  {cat}
                </span>
              );
            })}
          </div>

          {vendor.description && (
            <p className="vmp-spot-desc">{vendor.description}</p>
          )}

          {(vendor.instagram || vendor.facebook || vendor.website) && (
            <div className="vmp-spot-links">
              {vendor.instagram && (
                <a
                  href={`https://instagram.com/${vendor.instagram.replace(/^@/, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vmp-spot-link"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="5"/>
                    <circle cx="12" cy="12" r="5"/>
                    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
                  </svg>
                  {vendor.instagram.startsWith("@")
                    ? vendor.instagram
                    : `@${vendor.instagram}`}
                </a>
              )}
              {vendor.facebook && (
                <a
                  href={`https://facebook.com/${vendor.facebook}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vmp-spot-link"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                  </svg>
                  {vendor.facebook}
                </a>
              )}
              {vendor.website && (
                <a
                  href={vendor.website.startsWith("http")
                    ? vendor.website
                    : `https://${vendor.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vmp-spot-link"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                  </svg>
                  Website
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}